/**
 * Detect outline changes that affect linked documents.
 * Compares original sections (snapshot at edit start) with current sections.
 * Only considers sections with a nextgenDocumentId.
 *
 * @param {Array} originalSections - Sections snapshot from when edit mode started
 * @param {Array} currentSections - Current sections after user edits
 * @returns {{
 *   hasDocumentChanges: boolean,
 *   affectedDocuments: Array<{
 *     documentId: number,
 *     documentName: string,
 *     renames: Array<{outlineItemId: number, originalTitle: string, newTitle: string}>,
 *     deletions: Array<{outlineItemId: number, title: string}>,
 *     levelChanges: Array<{outlineItemId: number, title: string, originalLevel: number, newLevel: number}>,
 *     orderChanged: boolean
 *   }>,
 *   syncPayload: {
 *     affectedDocumentIds: number[],
 *     renames: Array<{outlineItemId: number, newTitle: string}>,
 *     deletions: Array<{outlineItemId: number}>,
 *     levelChanges: Array<{outlineItemId: number, newLevel: number}>,
 *     reorders: Array<{documentId: number, orderedOutlineItemIds: number[]}>
 *   }
 * }}
 */
const EMPTY_RESULT = {
  hasDocumentChanges: false,
  affectedDocuments: [],
  syncPayload: null,
};

const isPersistedSection = (section) =>
  section.id && !/^TEMP-/.test(section.id);

function indexLinkedSections(sections) {
  const byId = new Map();
  const docGroups = new Map();

  for (const section of sections) {
    if (!isPersistedSection(section) || !section.nextgenDocumentId) {
      continue;
    }

    byId.set(section.id, section);
    if (!docGroups.has(section.nextgenDocumentId)) {
      docGroups.set(section.nextgenDocumentId, []);
    }
    docGroups.get(section.nextgenDocumentId).push(section);
  }

  return { byId, docGroups };
}

function indexCurrentSections(sections) {
  const ids = new Set();
  const byId = new Map();

  for (const section of sections) {
    if (!isPersistedSection(section)) {
      continue;
    }
    ids.add(section.id);
    byId.set(section.id, section);
  }

  return { ids, byId };
}

function ensureDocEntry(perDoc, docId, documentName) {
  if (!perDoc.has(docId)) {
    perDoc.set(docId, {
      documentId: docId,
      documentName,
      renames: [],
      deletions: [],
      levelChanges: [],
      orderChanged: false,
    });
  }
  return perDoc.get(docId);
}

function findRenamesAndDeletions(originalById, currentIds, currentById) {
  const allRenames = [];
  const allDeletions = [];
  const allLevelChanges = [];
  const perDoc = new Map();

  for (const [itemId, original] of originalById) {
    const docId = original.nextgenDocumentId;

    if (!currentIds.has(itemId)) {
      allDeletions.push({ outlineItemId: itemId });
      ensureDocEntry(perDoc, docId, original.nextgenFileName).deletions.push({
        outlineItemId: itemId,
        title: original.title,
      });
      continue;
    }

    const current = currentById.get(itemId);
    if (current && current.title !== original.title) {
      allRenames.push({ outlineItemId: itemId, newTitle: current.title });
      ensureDocEntry(perDoc, docId, original.nextgenFileName).renames.push({
        outlineItemId: itemId,
        originalTitle: original.title,
        newTitle: current.title,
      });
    }

    if (current && current.level !== original.level) {
      allLevelChanges.push({ outlineItemId: itemId, newLevel: current.level });
      ensureDocEntry(perDoc, docId, original.nextgenFileName).levelChanges.push(
        {
          outlineItemId: itemId,
          title: current.title,
          originalLevel: original.level,
          newLevel: current.level,
        }
      );
    }
  }

  return { allRenames, allDeletions, allLevelChanges, perDoc };
}

function findReorders(
  originalDocGroups,
  originalById,
  currentSections,
  currentIds,
  perDoc
) {
  const reorders = [];

  for (const [docId, originalItems] of originalDocGroups) {
    const originalOrder = originalItems
      .map((item) => item.id)
      .filter((id) => currentIds.has(id));

    const currentOrder = currentSections
      .filter(
        (s) =>
          isPersistedSection(s) &&
          originalById.has(s.id) &&
          originalById.get(s.id).nextgenDocumentId === docId
      )
      .map((s) => s.id);

    const changed =
      currentOrder.length === originalOrder.length &&
      currentOrder.some((id, i) => id !== originalOrder[i]);

    if (changed) {
      ensureDocEntry(
        perDoc,
        docId,
        originalItems[0]?.nextgenFileName
      ).orderChanged = true;
      reorders.push({ documentId: docId, orderedOutlineItemIds: currentOrder });
    }
  }

  return reorders;
}

export function detectOutlineToDocChanges(originalSections, currentSections) {
  if (!originalSections?.length || !currentSections?.length) {
    return EMPTY_RESULT;
  }

  const { byId: originalById, docGroups } =
    indexLinkedSections(originalSections);
  if (originalById.size === 0) {
    return EMPTY_RESULT;
  }

  const { ids: currentIds, byId: currentById } =
    indexCurrentSections(currentSections);
  const { allRenames, allDeletions, allLevelChanges, perDoc } =
    findRenamesAndDeletions(originalById, currentIds, currentById);
  const reorders = findReorders(
    docGroups,
    originalById,
    currentSections,
    currentIds,
    perDoc
  );

  const affectedDocuments = Array.from(perDoc.values()).filter(
    (doc) =>
      doc.renames.length > 0 ||
      doc.deletions.length > 0 ||
      doc.levelChanges.length > 0 ||
      doc.orderChanged
  );

  if (affectedDocuments.length === 0) {
    return EMPTY_RESULT;
  }

  return {
    hasDocumentChanges: true,
    affectedDocuments,
    syncPayload: {
      affectedDocumentIds: affectedDocuments.map((d) => d.documentId),
      renames: allRenames,
      deletions: allDeletions,
      levelChanges: allLevelChanges,
      reorders,
    },
  };
}
